import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Search, Edit3, Loader2, X, Upload as UploadIcon, Save, RefreshCw, CheckSquare, Square, SlidersHorizontal, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { HighlightedText } from '../../components/HighlightedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same web-optimizer as the main-site admin: ≤1600px longest side, JPEG q0.82.
const optimizeImage = (file, maxDim = 1600, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', quality), width: w, height: h });
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const money = (n) => `$${Number(n ?? 0).toFixed(2)}`;

const GovAdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bulk edit mode
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // { field, mode, value, scope }
  const [bulkSaving, setBulkSaving] = useState(false);

  // Delete confirmation (single or bulk)
  const [deleteDialog, setDeleteDialog] = useState(null); // { kind: 'single'|'bulk', product?, ids? }
  const [deleting, setDeleting] = useState(false);

  // Debounce search input by 300ms so smart-search doesn't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (debouncedQ.trim().length >= 1) params.q = debouncedQ.trim();
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const [p, c] = await Promise.all([
        axios.get(`${API}/products`, { params }),
        axios.get(`${API}/admin/categories`).catch(() => ({ data: [] })),
      ]);
      setProducts(p.data || []);
      setCategories(c.data || []);
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [debouncedQ, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (p) => setEditing({
    id: p.id,
    name: p.name || '',
    price: p.price != null ? String(p.price) : '',
    retail_price: p.retail_price != null ? String(p.retail_price) : '',
    image: p.image || '',
  });

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        price: parseFloat(editing.price),
        retail_price: editing.retail_price === '' ? null : parseFloat(editing.retail_price),
        image: editing.image,
      };
      await axios.put(`${API}/products/${editing.id}`, payload);
      toast({ title: 'Product updated', description: editing.name });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const { dataUrl, width, height } = await optimizeImage(f);
      setEditing((prev) => prev ? { ...prev, image: dataUrl } : prev);
      toast({ title: 'Image optimized', description: `Web-optimized to ${width}×${height}` });
    } catch (err) {
      toast({ title: 'Image error', description: err?.message || 'Could not process image', variant: 'destructive' });
    } finally { e.target.value = ''; }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllVisible = () => setSelected(new Set(products.map((p) => p.id)));
  const clearSelection = () => setSelected(new Set());
  const exitBulkMode = () => { setBulkMode(false); clearSelection(); };

  const applyBulk = async () => {
    if (!bulkDialog) return;
    setBulkSaving(true);
    try {
      const body = {
        field: bulkDialog.field,
        mode: bulkDialog.mode,
        value: parseFloat(bulkDialog.value),
      };
      if (bulkDialog.scope === 'selection') body.ids = Array.from(selected);
      else if (bulkDialog.scope === 'category' && categoryFilter !== 'all') body.category = categoryFilter;
      const r = await axios.post(`${API}/products/bulk-update`, body);
      toast({ title: 'Bulk update applied', description: `Updated ${r.data.updated} of ${r.data.matched} product(s)` });
      setBulkDialog(null);
      clearSelection();
      load();
    } catch (e) {
      toast({ title: 'Bulk update failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBulkSaving(false); }
  };

  const applyCategoryMove = async () => {
    if (!bulkDialog || !bulkDialog.to_category) return;
    setBulkSaving(true);
    try {
      const body = { to_category: bulkDialog.to_category };
      if (bulkDialog.scope === 'selection') body.ids = Array.from(selected);
      else if (bulkDialog.scope === 'category' && categoryFilter !== 'all') body.from_category = categoryFilter;
      const r = await axios.post(`${API}/products/bulk-category`, body);
      toast({ title: 'Category updated', description: `Moved ${r.data.updated} product(s) → ${bulkDialog.to_category}` });
      setBulkDialog(null);
      clearSelection();
      load();
    } catch (e) {
      toast({ title: 'Category move failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBulkSaving(false); }
  };

  const allSelected = products.length > 0 && products.every((p) => selected.has(p.id));

  const confirmDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      if (deleteDialog.kind === 'single') {
        await axios.delete(`${API}/products/${deleteDialog.product.id}`);
        toast({ title: 'Product deleted', description: deleteDialog.product.name });
      } else {
        const r = await axios.post(`${API}/products/bulk-delete`, { ids: Array.from(selected) });
        toast({ title: 'Products deleted', description: `Removed ${r.data.deleted} item(s)` });
        clearSelection();
      }
      setDeleteDialog(null);
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4" data-testid="portal-admin-products-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Package className="h-6 w-6 text-blue-700 mt-0.5" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Product Editor</h1>
            <p className="text-sm text-slate-500 max-w-xl mt-1">
              Search and edit the master product catalog. Multi-word queries match each token independently — try <code className="text-[11px] bg-slate-100 px-1 rounded">CLASP 97</code>.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} size="sm" data-testid="portal-admin-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
        <Button variant={bulkMode ? 'default' : 'outline'} size="sm" onClick={() => bulkMode ? exitBulkMode() : setBulkMode(true)} data-testid="portal-admin-bulk-toggle" className={bulkMode ? 'bg-blue-700 hover:bg-blue-800 text-white' : ''}>
          <SlidersHorizontal className="h-4 w-4 mr-1" /> {bulkMode ? 'Exit Bulk Edit' : 'Bulk Edit'}
        </Button>
      </div>

      {bulkMode && (
        <div className="flex flex-wrap items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="portal-admin-bulk-toolbar">
          <div className="text-sm text-slate-700"><b>{selected.size}</b> selected</div>
          <Button size="sm" variant="outline" onClick={selectAllVisible}>Select all {products.length}</Button>
          {selected.size > 0 && <Button size="sm" variant="ghost" onClick={clearSelection}>Clear</Button>}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={selected.size === 0}
                    onClick={() => setBulkDialog({ field: 'price', mode: 'percent', value: '10', scope: 'selection' })}
                    data-testid="portal-admin-bulk-portal-pct">
              Adjust Portal Price %
            </Button>
            <Button size="sm" variant="outline" disabled={selected.size === 0}
                    onClick={() => setBulkDialog({ field: 'retail_price', mode: 'percent', value: '10', scope: 'selection' })}
                    data-testid="portal-admin-bulk-retail-pct">
              Adjust Retail Price %
            </Button>
            <Button size="sm" variant="outline" disabled={selected.size === 0}
                    onClick={() => setBulkDialog({ field: 'retail_price', mode: 'set', value: '', scope: 'selection' })}>
              Set Retail Price to $…
            </Button>
            <Button size="sm" variant="outline" disabled={selected.size === 0}
                    onClick={() => setBulkDialog({ mode: 'category_move', scope: 'selection', to_category: '' })}
                    data-testid="portal-admin-bulk-move-cat">
              Move to Category…
            </Button>
            {categoryFilter !== 'all' && (
              <Button size="sm" variant="outline"
                      onClick={() => setBulkDialog({ field: 'price', mode: 'percent', value: '10', scope: 'category' })}
                      data-testid="portal-admin-bulk-category-rule">
                Apply rule to whole &quot;{categoryFilter}&quot;
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={selected.size === 0}
                    onClick={() => setDeleteDialog({ kind: 'bulk' })}
                    className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                    data-testid="portal-admin-bulk-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Smart search — try 'CLASP 97'"
                className="pl-9"
                data-testid="portal-admin-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-52" data-testid="portal-admin-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id || c.slug} value={c.slug}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-500 ml-auto">
              {loading ? 'Loading…' : `${products.length} product${products.length === 1 ? '' : 's'}`}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No products match.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px]">
                <tr>
                  {bulkMode && (
                    <th className="p-3 w-8">
                      <button type="button" onClick={() => allSelected ? clearSelection() : selectAllVisible()} data-testid="portal-admin-select-all">
                        {allSelected ? <CheckSquare className="h-4 w-4 text-blue-700" /> : <Square className="h-4 w-4 text-slate-400" />}
                      </button>
                    </th>
                  )}
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">SKU</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-right p-3">Portal</th>
                  <th className="text-right p-3">Retail</th>
                  <th className="text-right p-3">Stock</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className={`border-t hover:bg-slate-50 ${bulkMode && selected.has(p.id) ? 'bg-blue-50/60' : ''}`} data-testid={`portal-admin-row-${p.id}`}>
                    {bulkMode && (
                      <td className="p-3">
                        <button type="button" onClick={() => toggleSelect(p.id)} data-testid={`portal-admin-row-check-${p.id}`}>
                          {selected.has(p.id) ? <CheckSquare className="h-4 w-4 text-blue-700" /> : <Square className="h-4 w-4 text-slate-400" />}
                        </button>
                      </td>
                    )}
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {p.image && <img src={p.image} alt={p.name} className="h-10 w-10 object-cover rounded border" />}
                        <div>
                          <div className="font-medium text-slate-800">
                            <HighlightedText text={p.name} query={debouncedQ} />
                          </div>
                          <div className="text-[11px] text-slate-400 line-clamp-1 max-w-md">{p.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-slate-500 font-mono text-[11px]"><HighlightedText text={p.sku || ''} query={debouncedQ} /></td>
                    <td className="p-3 text-slate-500 text-[11px]">{p.category}</td>
                    <td className="p-3 text-right text-slate-800">{money(p.price)}</td>
                    <td className="p-3 text-right text-slate-800">{p.retail_price != null ? money(p.retail_price) : <span className="text-slate-300">—</span>}</td>
                    <td className="p-3 text-right text-slate-500">{p.stock ?? 0}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`portal-admin-edit-${p.id}`}>
                          <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => setDeleteDialog({ kind: 'single', product: p })}
                                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                                title="Delete product"
                                data-testid={`portal-admin-delete-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !saving && setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="portal-admin-edit-dialog">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="font-semibold text-slate-800">Edit Product</h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setEditing(null)}><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label htmlFor="pa-name">Name</Label>
                <Input id="pa-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="portal-admin-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pa-price">Portal / Contract Price</Label>
                  <Input id="pa-price" type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="portal-admin-price" />
                </div>
                <div>
                  <Label htmlFor="pa-retail">Retail Price</Label>
                  <Input id="pa-retail" type="number" step="0.01" value={editing.retail_price} onChange={(e) => setEditing({ ...editing, retail_price: e.target.value })} placeholder="Optional" data-testid="portal-admin-retail" />
                </div>
              </div>
              <div>
                <Label htmlFor="pa-image">Image</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input id="pa-image" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="Image URL or upload →" />
                  <input id="pa-image-upload" type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="portal-admin-image-upload" />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('pa-image-upload')?.click()}>
                    <UploadIcon className="h-4 w-4 mr-1" /> Upload
                  </Button>
                </div>
                {editing.image && <img src={editing.image} alt="preview" className="mt-2 h-24 w-24 object-cover rounded border" />}
                <p className="text-[10px] text-slate-500 mt-1">Uploads auto-resize to a Web-Optimized JPEG (≤1600px, ~q82).</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-3">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="portal-admin-save">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {bulkDialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !bulkSaving && setBulkDialog(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="portal-admin-bulk-dialog">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="font-semibold text-slate-800">
                {bulkDialog.mode === 'category_move' ? 'Move to Category'
                  : bulkDialog.mode === 'percent' ? `Adjust ${bulkDialog.field === 'price' ? 'Portal Price' : 'Retail Price'} by %`
                  : `Set ${bulkDialog.field === 'price' ? 'Portal Price' : 'Retail Price'} to $…`}
              </h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setBulkDialog(null)}><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-slate-600 bg-slate-50 border rounded p-2">
                <b>Scope:</b> {bulkDialog.scope === 'selection'
                    ? `${selected.size} selected product${selected.size === 1 ? '' : 's'}`
                    : `every product in "${categoryFilter}"`}
              </div>
              {bulkDialog.mode === 'category_move' ? (
                <div>
                  <Label htmlFor="pa-bulk-cat">Target category</Label>
                  <Select value={bulkDialog.to_category || ''} onValueChange={(v) => setBulkDialog({ ...bulkDialog, to_category: v })}>
                    <SelectTrigger data-testid="portal-admin-bulk-target-cat"><SelectValue placeholder="Choose category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id || c.slug} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label htmlFor="pa-bulk-value">{bulkDialog.mode === 'percent' ? 'Percent change' : 'New value ($)'}</Label>
                  <div className="flex items-center gap-2">
                    <Input id="pa-bulk-value" type="number" step="0.01" value={bulkDialog.value}
                           onChange={(e) => setBulkDialog({ ...bulkDialog, value: e.target.value })}
                           placeholder={bulkDialog.mode === 'percent' ? 'e.g. 10 or -5' : 'e.g. 29.99'}
                           data-testid="portal-admin-bulk-value" />
                    <span className="text-sm text-slate-500">{bulkDialog.mode === 'percent' ? '%' : '$'}</span>
                  </div>
                  {bulkDialog.mode === 'percent' && (
                    <p className="text-[11px] text-slate-500 mt-1">Positive value increases prices; negative decreases. E.g. <code>-10</code> = 10% off.</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-3">
              <Button variant="outline" onClick={() => setBulkDialog(null)} disabled={bulkSaving}>Cancel</Button>
              <Button
                onClick={bulkDialog.mode === 'category_move' ? applyCategoryMove : applyBulk}
                disabled={bulkSaving || (bulkDialog.mode !== 'category_move' && !bulkDialog.value) || (bulkDialog.mode === 'category_move' && !bulkDialog.to_category)}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="portal-admin-bulk-apply">
                {bulkSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Apply
              </Button>
            </div>
          </div>
        </div>
      )}
      {deleteDialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !deleting && setDeleteDialog(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="portal-admin-delete-dialog">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="font-semibold text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Confirm Deletion
              </h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setDeleteDialog(null)}><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm text-slate-700">
              {deleteDialog.kind === 'single' ? (
                <>
                  <p>You are about to permanently delete this product from the database:</p>
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <div className="font-medium text-slate-800">{deleteDialog.product.name}</div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      SKU: <span className="font-mono">{deleteDialog.product.sku || '—'}</span> · Category: {deleteDialog.product.category}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p>You are about to permanently delete <b>{selected.size}</b> selected product{selected.size === 1 ? '' : 's'} from the database.</p>
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-[12px] text-red-800">
                    This bulk action cannot be undone. Orders that reference these products will keep their historical line-item data, but the products will disappear from search and browsing immediately.
                  </div>
                </>
              )}
              <p className="text-[12px] text-slate-500">This action is irreversible.</p>
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-3">
              <Button variant="outline" onClick={() => setDeleteDialog(null)} disabled={deleting}>Cancel</Button>
              <Button onClick={confirmDelete} disabled={deleting}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="portal-admin-delete-confirm">
                {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                {deleteDialog.kind === 'single' ? 'Delete Product' : `Delete ${selected.size} Product${selected.size === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GovAdminProducts;
